import {
  Scene,
  WebGLRenderer,
} from "three";
import type ThreeJSApp from "../../app";
import Size from "../../utils/size";
import Camera from "../camera/camera";
import { BloomEffect } from "./effects/BloomEffect";
import { defaultRendererConfig } from "../../config/default.config";
import {
  createWebGLRenderer,
  createBloomEffect,
} from "./core";
import {
  setRendererSize,
  getCanvasSize,
  disposeRenderer,
  disposeBloomEffect,
  isBloomEnabled,
  renderBloom,
  updateBloomParams,
  updateBlendParams,
  setBloomRenderMode,
  highlightBloomedObjects,
  getBloomParams,
  getBlendParams,
  getCurrentRenderMode,
  setInitialRendererSize,
  resizeBloomEffect,
  getDefaultRendererConfig,
  getDefaultBloomParams,
  getDefaultBlendParams,
  calculatePixelRatio
} from "./util";
import type {
  RendererConfig,
  BloomParams,
  BlendParams,
  RenderMode
} from "./type";

export default class Renderer {
  declare root: ThreeJSApp;
  declare camera: Camera;

  canvas: HTMLCanvasElement;
  size: Size;
  scene: Scene;
  instance!: WebGLRenderer;
  config: RendererConfig;

  // 블룸 효과 (선택적으로 활성화)
  private bloomEffect?: BloomEffect;

  constructor(root: ThreeJSApp, customConfig?: Partial<RendererConfig>) {
    this.root = root;
    this.canvas = this.root.canvas;
    this.size = this.root.size;
    this.scene = this.root.scene;
    this.camera = this.root.camera;

    // 설정 병합
    this.config = this.mergeConfig(customConfig);

    this.initialize();
  }

  private mergeConfig(customConfig?: Partial<RendererConfig>): RendererConfig {
    const defaultConfig = defaultRendererConfig;

    if (!customConfig) return defaultConfig;

    // 깊은 병합 처리
    const mergedConfig: RendererConfig = {
      renderer: {
        ...defaultConfig.renderer,
        ...customConfig.renderer,
        shadow: {
          ...defaultConfig.renderer?.shadow,
          ...customConfig.renderer?.shadow
        }
      },
      bloom: {
        ...defaultConfig.bloom,
        ...customConfig.bloom,
        config: {
          bloomParams: {
            ...defaultConfig.bloom?.config?.bloomParams,
            ...customConfig.bloom?.config?.bloomParams
          },
          blendParams: {
            ...defaultConfig.bloom?.config?.blendParams,
            ...customConfig.bloom?.config?.blendParams
          }
        }
      }
    };

    return mergedConfig;
  }

  // 렌더러 설정 동적 업데이트
  updateConfig(newConfig: RendererConfig) {

    // 새로운 설정 적용
    this.config = newConfig;

    // 기존 블룸 효과 정리
    if (this.bloomEffect) {
      disposeBloomEffect(this.bloomEffect);
      this.bloomEffect = undefined;
    }

    // 새로운 블룸 효과 설정
    this.setupBloomEffect();

    // 블룸 효과 크기 동기화 - 현재 렌더러 크기로 즉시 조정
    if (this.bloomEffect) {
      const { width, height } = getCanvasSize(this.canvas, this.size.width, this.size.height);
      resizeBloomEffect(this.bloomEffect, width, height);
      
    }

    
  }

  private initialize() {
    this.setInstance();
    this.setupBloomEffect();
    this.resize();
  }

  private setInstance() {
    this.instance = createWebGLRenderer(this.canvas, this.config.renderer);

    // 초기 렌더러 크기 설정
    setInitialRendererSize(this.instance, this.canvas);
  }

  private setupBloomEffect() {
    // 블룸 효과가 활성화된 경우에만 생성
    if (isBloomEnabled(this.config)) {
      const { width, height } = getCanvasSize(this.canvas, this.size.width, this.size.height);
      this.bloomEffect = createBloomEffect(
        this.scene,
        this.instance,
        this.camera.perspectiveCamera,
        width,
        height,
        this.config.bloom?.config
      );
    }
  }

  // 렌더러 재생성
  recreateRenderer() {
    

    // 기존 리소스 정리
    disposeRenderer(this.instance);
    if (this.bloomEffect) {
      disposeBloomEffect(this.bloomEffect);
      this.bloomEffect = undefined;
    }

    // 새로운 인스턴스 생성
    this.setInstance();
    this.setupBloomEffect();

    
  }

  // 크기 조정
  resize(width?: number, height?: number) {
    const { width: finalWidth, height: finalHeight } = this.getFinalSize(width, height);

    // 렌더러 크기 설정
    const pixelRatio = calculatePixelRatio(this.config.renderer?.maxPixelRatio ?? getDefaultRendererConfig().maxPixelRatio);
    setRendererSize(this.instance, this.canvas, finalWidth, finalHeight, pixelRatio);

    // 블룸 효과 크기 조정 (활성화된 경우만)
    if (this.bloomEffect) {
      resizeBloomEffect(this.bloomEffect, finalWidth, finalHeight);
    }

  }

  private getFinalSize(width?: number, height?: number): { width: number; height: number } {
    if (width !== undefined && height !== undefined) {
      return { width, height };
    }

    const { width: canvasWidth, height: canvasHeight } = getCanvasSize(
      this.canvas,
      this.size.width,
      this.size.height
    );

    return { width: canvasWidth, height: canvasHeight };
  }

  // 렌더링
  update() {
    if (this.bloomEffect) {
      renderBloom(this.bloomEffect);
    } else {
      // 블룸 효과가 비활성화된 경우 일반 렌더링
      this.instance.render(this.scene, this.camera.perspectiveCamera);
    }
  }

  // 블룸 파라미터 업데이트 (블룸 효과가 활성화된 경우만)
  updateBloomParams(params: Partial<BloomParams>) {
    if (this.bloomEffect) {
      updateBloomParams(this.bloomEffect, params);
    } else {
      console.warn('⚠️ 블룸 효과가 비활성화되어 있습니다.');
    }
  }

  // 블렌드 파라미터 업데이트 (블룸 효과가 활성화된 경우만)
  updateBlendParams(params: Partial<BlendParams>) {
    if (this.bloomEffect) {
      updateBlendParams(this.bloomEffect, params);
    } else {
      console.warn('⚠️ 블룸 효과가 비활성화되어 있습니다.');
    }
  }

  // 렌더 모드 변경 (블룸 효과가 활성화된 경우만)
  setRenderMode(mode: RenderMode) {
    if (this.bloomEffect) {
      setBloomRenderMode(this.bloomEffect, mode);
    } else {
      console.warn('⚠️ 블룸 효과가 비활성화되어 있습니다.');
    }
  }

  // 블룸 객체 하이라이트 (블룸 효과가 활성화된 경우만)
  highlightBloomedObjects() {
    if (this.bloomEffect) {
      highlightBloomedObjects(this.bloomEffect);
    } else {
      console.warn('⚠️ 블룸 효과가 비활성화되어 있습니다.');
    }
  }

  // 현재 설정값 getter
  getBloomParams(): BloomParams | null {
    return this.bloomEffect ? getBloomParams(this.bloomEffect) : null;
  }

  getBlendParams(): BlendParams | null {
    return this.bloomEffect ? getBlendParams(this.bloomEffect) : null;
  }

  getCurrentRenderMode(): RenderMode | null {
    return this.bloomEffect ? getCurrentRenderMode(this.bloomEffect) : null;
  }

  // 블룸 효과 활성화 여부 확인
  isBloomEnabled(): boolean {
    return Boolean(this.bloomEffect);
  }

  // 현재 렌더러 설정 반환
  getConfig(): RendererConfig {
    return this.config;
  }

  // 기본 설정값 getter
  getDefaultRendererConfig() {
    return getDefaultRendererConfig();
  }

  getDefaultBloomParams() {
    return getDefaultBloomParams();
  }

  getDefaultBlendParams() {
    return getDefaultBlendParams();
  }

  // 리소스 정리
  dispose() {
    if (this.bloomEffect) {
      disposeBloomEffect(this.bloomEffect);
    }
    if (this.instance) {
      disposeRenderer(this.instance);
    }
  }
}